/**
 * Activate entry page (Figma 791:18211).
 *
 * Validates the activation code the customer typed, persists it with the
 * chosen province, then hands off to the activation form page. The invalid
 * state is the design's own 791:18324 frame: inline red line under the
 * input, nothing else changes.
 *
 * What the customer types is the 9-digit activation code PRINTED ON THE SIM
 * and nothing else (Matt Callahan, end-to-end testing session 15 Sep 2026:
 * "they should just be entering the nine digit activation code", and the
 * issuer prefix stays hidden because "the SIM card itself, it's printed, it
 * says activation code — if we have something pre-populated in there it's
 * going to be a disconnect"). We assemble the full ICCID ourselves by
 * prepending the issuer prefix, so what travels onward is always the whole
 * 19-digit number Ztar's activate call expects. A pasted full ICCID is
 * still accepted so existing confirmation links keep working.
 *
 * Province uses the same sessionStorage key (speakout.province_code) the
 * plans flow already carries, so a province picked anywhere on the site
 * pre-fills here and vice versa.
 */
(function () {
  'use strict';

  var PROVINCE_KEY = 'speakout.province_code';
  var CODE_KEY = 'speakout.activation_code';

  function init(root) {
    if (root._speakoutActivateEntry) return;
    root._speakoutActivateEntry = true;

    var input = root.querySelector('[data-activation-code-input]');
    var error = root.querySelector('[data-activation-code-error]');
    var continueBtn = root.querySelector('[data-activation-continue]');
    var provinceSelect = root.querySelector('[data-province-row] select');

    function carriedProvince() {
      try {
        return sessionStorage.getItem(PROVINCE_KEY);
      } catch (e) {
        return null;
      }
    }

    if (provinceSelect) {
      var carried = carriedProvince();
      if (carried && provinceSelect.querySelector('option[value="' + CSS.escape(carried) + '"]')) {
        provinceSelect.value = carried;
      }
      provinceSelect.addEventListener('change', function () {
        try {
          sessionStorage.setItem(PROVINCE_KEY, provinceSelect.value);
        } catch (e) {
          /* private mode — the value still travels via the URL below */
        }
      });
    }

    // Every SpeakOut SIM number (ICCID) starts with the same issuer prefix
    // (Rana Haddad, Marker SQU-3, 10 Sep 2026: "All activation codes should
    // start with 8912230000"). The prefix is a section setting so a new
    // SIM batch never needs a code change.
    var iccidPrefix = (root.dataset.iccidPrefix || '').replace(/\D/g, '');

    // Returns the value to carry onward: the full ICCID whenever we can
    // build one, so the activation form and the Ztar payload never have to
    // know about the prefix. Returns null when the input is not usable.
    function validCode(value) {
      var cleaned = value.replace(/[\s-]+/g, '');
      // The 9 digits printed on the SIM. Prepend the issuer prefix.
      if (/^\d{9}$/.test(cleaned)) {
        return iccidPrefix ? iccidPrefix + cleaned : cleaned;
      }
      // Legacy alphanumeric activation code: travels as-is, unchanged.
      if (/^[A-Za-z0-9]{9}$/.test(cleaned)) return cleaned;
      // A full ICCID pasted or carried in from a confirmation link.
      if (/^\d{19,20}$/.test(cleaned)) {
        if (iccidPrefix && cleaned.indexOf(iccidPrefix) !== 0) return null;
        return cleaned;
      }
      return null;
    }

    function showError(show) {
      if (!error) return;
      error.hidden = !show;
      if (input) input.classList.toggle('has-error', show);
    }

    if (input) {
      input.addEventListener('input', function () {
        showError(false);
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        var code = input ? validCode(input.value) : null;
        if (!code) {
          showError(true);
          if (input) input.focus();
          return;
        }
        var province = provinceSelect ? provinceSelect.value : '';
        try {
          sessionStorage.setItem(CODE_KEY, code);
          if (province) sessionStorage.setItem(PROVINCE_KEY, province);
        } catch (e) {
          /* carried via URL params below regardless */
        }
        var url = root.dataset.formUrl || '/pages/activation';
        var params = new URLSearchParams();
        params.set('code', code);
        if (province) params.set('province', province);
        window.location.assign(url + '?' + params.toString());
      });
    }
  }

  document.querySelectorAll('.speakout-activate-entry').forEach(init);
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.speakout-activate-entry');
    if (root) init(root);
  });
})();
